import { useState } from "react";
import { ActivityIndicator, Modal, Share, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text, Icon } from "react-native-paper";

import { fetchReferenceLink } from "@/lib/onboarding-api";
import { colors } from "@/lib/theme";

interface ShareReferenceButtonProps {
    referenceName: string;
    rnFullName: string;
    visible: boolean;
    /** Needed to mint the reference's own link. */
    rnId: number | null;
    subsectionId: number | null;
}

export function ShareReferenceButton({
    referenceName,
    rnFullName,
    visible,
    rnId,
    subsectionId
}: ShareReferenceButtonProps) {
    const [modalVisible, setModalVisible] = useState(false);
    const [sharing, setSharing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!visible) {
        return null;
    }

    async function handleShare() {
        if (rnId === null || subsectionId === null) {
            setError("We couldn't prepare your link. Please try again in a moment.");
            return;
        }
        setSharing(true);
        setError(null);
        try {
            // The link is minted server-side from the contact details she already
            // typed, so sharing without one is not possible — that was the whole
            // gap: the old message asked them to expect a call and gave them
            // nothing to act on.
            const { url } = await fetchReferenceLink(rnId, subsectionId);
            await Share.share({
                message:
                    `Hi ${referenceName}, this is ${rnFullName}. ` +
                    `I'm joining Task Health (a nursing platform) and listed you as a work reference. ` +
                    `Could you fill in this short form? It takes about a minute:\n\n${url}\n\n` +
                    `Thank you so much for helping me out!`
            });
            setModalVisible(false);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Something went wrong. Please try again.");
        } finally {
            setSharing(false);
        }
    }

    return (
        <>
            <TouchableOpacity style={styles.triggerButton} onPress={() => setModalVisible(true)}>
                <Icon source="share-variant" size={20} color="#FFFFFF" />
                <Text style={styles.triggerButtonText}>Share with your Reference</Text>
            </TouchableOpacity>

            <Modal
                visible={modalVisible}
                transparent
                animationType="fade"
                onRequestClose={() => setModalVisible(false)}
            >
                <View style={styles.overlay}>
                    <View style={styles.modalContainer}>
                        <Text style={styles.modalTitle}>Share with your Reference</Text>

                        <Text style={styles.modalBody}>
                            The fastest way is to send it yourself, so it comes from you. Tap Share
                            and we'll include a link they can fill in on their phone in about a
                            minute.{"\n\n"}We'll follow up with them directly too.
                        </Text>

                        {error !== null && <Text style={styles.errorText}>{error}</Text>}

                        <TouchableOpacity
                            style={[styles.shareButton, sharing && styles.shareButtonDisabled]}
                            onPress={handleShare}
                            disabled={sharing}
                        >
                            {sharing ? (
                                <ActivityIndicator size="small" color="#FFFFFF" />
                            ) : (
                                <Icon source="share-variant" size={20} color="#FFFFFF" />
                            )}
                            <Text style={styles.shareButtonText}>
                                {sharing ? "Preparing link" : "Share"}
                            </Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.closeButton}
                            onPress={() => setModalVisible(false)}
                        >
                            <Text style={styles.closeButtonText}>Close</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </Modal>
        </>
    );
}

const styles = StyleSheet.create({
    errorText: {
        color: colors.error600,
        fontSize: 14,
        marginBottom: 12,
        textAlign: "center"
    },
    shareButtonDisabled: { opacity: 0.6 },
    triggerButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        backgroundColor: colors.primary,
        borderRadius: 12,
        paddingVertical: 14,
        marginTop: 16,
        marginBottom: 8
    },
    triggerButtonText: {
        fontSize: 15,
        fontWeight: "700",
        color: "#FFFFFF"
    },
    overlay: {
        flex: 1,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        justifyContent: "center",
        alignItems: "center",
        padding: 24
    },
    modalContainer: {
        backgroundColor: colors.background,
        borderRadius: 16,
        padding: 24,
        width: "100%",
        maxWidth: 400
    },
    modalTitle: {
        fontSize: 18,
        fontWeight: "700",
        color: colors.textPrimary,
        textAlign: "center",
        marginBottom: 16
    },
    modalBody: {
        fontSize: 14,
        lineHeight: 22,
        color: colors.textSecondary,
        textAlign: "center",
        marginBottom: 24
    },
    shareButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        backgroundColor: colors.primary,
        borderRadius: 12,
        paddingVertical: 14
    },
    shareButtonText: {
        fontSize: 16,
        fontWeight: "700",
        color: "#FFFFFF"
    },
    closeButton: {
        alignItems: "center",
        paddingVertical: 14,
        marginTop: 4
    },
    closeButtonText: {
        fontSize: 15,
        fontWeight: "600",
        color: colors.textSecondary
    }
});
